/**
 * DeadlineRuleInlineEditor コンポーネント
 * FL-110: 取引先締切ルール管理機能 - インライン編集（複数曜日対応）
 * リファクタリング済み: カスタムフックとコンポーネント分割で簡素化
 */

import React, { useImperativeHandle } from 'react';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { DeadlineRuleInlineEditorProps, DeadlineRuleInlineEditorRef } from '@/types/deadlineRule';
import { useDeadlineRules } from '@/hooks/useDeadlineRules';
import { getDayOfWeekLabel, getWeekdayOrder } from '@/utils/deadlineRuleUtils';
import { WeekdayRuleSection } from './WeekdayRuleSection';

export const DeadlineRuleInlineEditor = React.forwardRef<
  DeadlineRuleInlineEditorRef,
  DeadlineRuleInlineEditorProps
>(({ rules, businessPartnerId, businessPartnerName, companyId, isLoading = false }, ref) => {
  const {
    weekdayRules,
    enabledDays,
    getModifiedRules,
    cancelChanges,
    handleWeekdayToggle,
    handlePickupToggle,
    handleDeliveryToggle,
    updateWeekdayRule,
  } = useDeadlineRules({
    rules,
    businessPartnerId,
    companyId,
  });

  // 親コンポーネントに関数を公開
  useImperativeHandle(
    ref,
    () => ({
      getModifiedRules,
      cancelChanges,
    }),
    [getModifiedRules, cancelChanges]
  );

  return (
    <div>
      {/* セクション見出し */}
      <div className='mb-4'>
        <h3 className='text-lg font-medium text-gray-900 dark:text-white'>締切ルール設定</h3>
        <p className='text-sm text-gray-500 dark:text-gray-400'>{businessPartnerName}</p>
      </div>

      {/* 曜日別ルール設定（統合） */}
      <div className='space-y-4'>
        {getWeekdayOrder().map(dayOfWeek => {
          const dayLabel = getDayOfWeekLabel(dayOfWeek);
          const dayRules = weekdayRules[dayOfWeek];
          const isEnabled = Boolean(enabledDays[dayOfWeek]);

          return (
            <WeekdayRuleSection
              key={dayOfWeek}
              dayOfWeek={dayOfWeek}
              dayLabel={dayLabel}
              dayRules={dayRules}
              isEnabled={isEnabled}
              isLoading={isLoading}
              onWeekdayToggle={handleWeekdayToggle}
              onPickupToggle={handlePickupToggle}
              onDeliveryToggle={handleDeliveryToggle}
              onRuleUpdate={updateWeekdayRule}
            />
          );
        })}

        {/* 空の状態メッセージ */}
        {Object.values(weekdayRules).every(
          dayRules =>
            (dayRules.pickup === null || dayRules.pickup?.isDeleted) &&
            (dayRules.delivery === null || dayRules.delivery?.isDeleted)
        ) && (
          <div className='text-center py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg'>
            <div className='text-gray-500 dark:text-gray-400'>
              <CalendarDaysIcon className='h-12 w-12 mx-auto mb-4 text-gray-400' />
              <p className='text-lg mb-2'>締切ルールが設定されていません</p>
              <p className='text-sm'>曜日にチェックを入れて締切ルールを設定してください</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

DeadlineRuleInlineEditor.displayName = 'DeadlineRuleInlineEditor';
